/* eslint-disable react/display-name */
/* eslint-disable no-restricted-imports */
import React, { Component, SyntheticEvent, createContext } from 'react';

import Form from '@rjsf/mui';
import { ArrayFieldTemplateProps, RJSFSchema, TitleFieldProps } from '@rjsf/utils';
import validator from '@rjsf/validator-ajv8';
import isEqual from 'lodash/isEqual';
import uniq from 'lodash/uniq';
import { WithSnackbarProps, withSnackbar } from 'notistack';

import { ExpandMore } from '@mui/icons-material';
import CheckIcon from '@mui/icons-material/Check';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';

import { Suppliers } from '@luxuryescapes/contract-svc-bedbank/constants/suppliers';

import ImageUploadField from '~/components/Common/Forms/ImageUploadField';
import ReactHookFormAdapter from '~/components/Common/Forms/widgets/ReactHookFormAdapter';

import { InternalProperty } from '~/services/AccommodationService';

import bedbankService from '../../../../services/BedbankService';
import { reportError } from '../../../../utils/reportError';
import SearchOrderingPane from '../../../Offers/Detail/SearchOrdering';
import RateInfo from '../Home/RateInfo';

import ArchivedPromotionPane from './ArchivedPromotionPane';
import BedbankRoomManagement from './BedbankRoomManagement';
import BedbankRoomMapping from './BedbankRoomMapping';
import {
  getHolidayTypesSchema,
  getPromotionsSchema,
  getRoomsSchema,
  getSchema,
  getSellPricesSchema,
  getTagsSchema,
  getVideosSchema,
  uiSchemas,
} from './FormSchemas';
import ImagesForm from './ImagesForm';
import PromotionMultiSelect from './PromotionMultiSelect';
import PromotionRoomMultiSelect from './PromotionRoomMultiSelect';
import PromotionsArrayFieldTemplate from './PromotionsArrayFieldTemplate';
import RankPane from './RankPane';
import ReviewsPane from './ReviewsPane';
import { TabPanel } from './utils/TabPanel';
import { isArchivedPromotion } from './utils/isArchivedPromotion';
import { mapPromotion } from './utils/mapPromotion';

interface EditFormProps {
  property: App.Bedbank.PropertyEdit;
  setProperty: (property: App.Bedbank.PropertyEdit) => void;
  onForceDecoupleRoomsClick: (ids: Array<string>) => void;
  isAdmin: boolean;
  accommodationInternalProperty?: InternalProperty;
}

const buttonMessages = {
  primary: 'Save',
  info: 'Saving...',
  success: 'Saved',
  error: 'Failed to save',
};

const hideSaveButtonTabs = [10, 11];

interface State {
  tabIndex: number;
  calendarFetching: boolean;
  calendar: {
    rooms: Array<{
      id: string;
      inclusive: number;
    }>;
  };
  property: App.Bedbank.PropertyEdit;
  accommodationInternalProperty?: InternalProperty;
  activePromotions: Array<App.Bedbank.PromotionsEdit>;
  archivedPromotions: Array<App.Bedbank.PromotionsEdit>;
  saveState: 'success' | 'primary' | 'error' | 'info';
  alertState: {
    message: string;
  } | null;
  schemas: {
    main: RJSFSchema;
    sellPrices: any;
    promotions: any;
    rooms: any;
    roomMapping: any;
    videos: any;
    campaigns: any;
    holidayTypes: any;
  };
  roomsExpansionStatuses: Array<boolean>;
  videos: Array<App.Bedbank.PropertyVideoResponse>;
  roomMappings: {
    update: App.Bedbank.SupplierRoomMappingPayload;
    remove: App.Bedbank.SupplierRoomMappingPayload;
    automappingFlagUpdate: App.Bedbank.SupplierRoomAutomappingFlagPayload;
  } | null;
  notistack: any;
  hash?: string;
  marketingTagsLoading: boolean;
}

function apiToStatePropertyMapper(apiRes: App.Bedbank.PropertyEdit) {
  return {
    ...apiRes,
    suppliers: apiRes.suppliers,
    promotions: apiRes.promotions.map((promotion) => mapPromotion(promotion, isArchivedPromotion(promotion))),
  };
}

const BedbankContext = createContext<State>({} as State);

function sellRoomTitleFieldTemplate(props: TitleFieldProps) {
  const { id } = props;
  const index = Number(id.match(/[0-9]+/i)[0]);
  const promoRooms = (this.state.property.promotions ?? [])
    .filter((p) => !isArchivedPromotion(p))
    .flatMap((p) => p.restrictToRooms ?? []);
  const filteredRooms = this.state.property.rooms.filter((r) => promoRooms.includes(r.id));
  const room = filteredRooms[index];
  return <legend id={id}>{room.nameOverride ?? room.name}</legend>;
}

function roomTitleFieldTemplate(props: TitleFieldProps) {
  const { id } = props;
  const index = Number(id.match(/[0-9]+/i)[0]);
  const room = this.state.property.rooms[index];
  const roomCalendar = (this.state.calendar.rooms ?? []).find((x) => x.id === room.id);
  return (
    <legend id={id}>
      {room.name} {roomCalendar && <CheckIcon color="success" />}
    </legend>
  );
}

function a11yProps(index: number) {
  return {
    id: `bedbank-tab-${index}`,
    'aria-controls': `bedbank-tabpanel-${index}`,
  };
}

function requestImageMapper(image) {
  return {
    id: image.id,
    hidden: image.hidden,
    order: image.order,
    title: image.title,
    is_hero: image.is_hero,
    is_deleted: image.is_deleted,
    source: image.source,
  };
}

type PatchableRoom = App.Bedbank.PropertyEdit['rooms'][number] & {
  nameOverrideSource?: 'manual' | 'accommodation' | null;
};

function roomDescriptionUpdateContext(props) {
  const { markdownId } = props;
  const index = Number(markdownId.match(/[0-9]+/i)[0]);
  const room = this.state.property.rooms[index];

  return {
    propertyName: this.props.property.name,
    roomName: room.name,
    inclusions: this.props.property.inclusions,
    model: 'room-type',
    field: 'description',
  };
}

class BedbankPropertiesEditForm extends Component<EditFormProps & WithSnackbarProps, State> {
  originalSupplierMarkups: EditFormProps['property']['suppliers'];
  originalRooms: ReturnType<BedbankPropertiesEditForm['stateToApiPropertyMapper']>['rooms'];
  originalDisableMarginMax: boolean;
  originalExtractRoomNameInclusions: boolean;
  constructor(props: EditFormProps & WithSnackbarProps) {
    super(props);

    const numberOfRoomAccordionElements = props.property.rooms.length;
    const arrayWithNumberOfAccordionElements = Array(numberOfRoomAccordionElements);
    const roomsExpansionElements = arrayWithNumberOfAccordionElements.fill(false);

    this.originalSupplierMarkups = props.property.suppliers;
    this.originalDisableMarginMax = !!props.property.disableMarginMax;
    this.originalExtractRoomNameInclusions = !!props.property.extractRoomNameInclusions;
    this.originalRooms = this.stateToApiPropertyMapper(apiToStatePropertyMapper(props.property)).rooms;

    this.state = {
      tabIndex: Number(window.location.hash?.replace('#', '') ?? 0),
      calendarFetching: false,
      calendar: {
        rooms: [],
      },
      property: apiToStatePropertyMapper(props.property),
      accommodationInternalProperty: props.accommodationInternalProperty,
      activePromotions: props.property.promotions.filter((promotion) => !isArchivedPromotion(promotion)),
      archivedPromotions: props.property.promotions.filter((promotion) => isArchivedPromotion(promotion)),
      saveState: 'primary',
      alertState: null,
      schemas: {
        main: getSchema(props.property.suppliers),
        sellPrices: getSellPricesSchema(),
        promotions: getPromotionsSchema(props.property),
        rooms: getRoomsSchema(),
        roomMapping: null,
        videos: getVideosSchema(),
        campaigns: null,
        holidayTypes: null,
      },
      roomsExpansionStatuses: roomsExpansionElements,
      videos: [],
      roomMappings: null,
      notistack: null,
      marketingTagsLoading: true,
    };
  }

  componentDidMount = async () => {
    this.fetchCalendarData();
    this.fetchMarketingTags();
  };

  fetchMarketingTags = async () => {
    try {
      this.setState({ marketingTagsLoading: true });
      const tagsSchema = await getTagsSchema();
      const holidayTypesSchema = await getHolidayTypesSchema();
      this.setState({
        schemas: {
          ...this.state.schemas,
          campaigns: tagsSchema,
          holidayTypes: holidayTypesSchema,
        },
        marketingTagsLoading: false,
      });
    } catch (error) {
      this.setState({ marketingTagsLoading: false });
      reportError(error);
      this.props.enqueueSnackbar('Error fetching marketing tags', { variant: 'error' });
    }
  };

  uiSchemas = (props: EditFormProps) => ({
    main: {
      ...uiSchemas(props).main,
    },
    sellPrices: {
      ...uiSchemas(props).sellPrices,
      rooms: {
        ...uiSchemas(props).sellPrices.rooms,
        items: {
          ...uiSchemas(props).sellPrices.rooms.items,
          'ui:TitleFieldTemplate': sellRoomTitleFieldTemplate.bind(this),
        },
      },
    },
    promotions: {
      ...uiSchemas(props).promotions,
      items: {
        ...uiSchemas(props).promotions.items,
        restrictToRooms: {
          'ui:widget': ReactHookFormAdapter,
          'ui:options': {
            ReactHookFormComponent: PromotionRoomMultiSelect,
            props: {
              property: this.state.property,
              setRooms: this.selectRoomsInForm,
              activePromotions: this.state.activePromotions,
            },
          },
        },
        restrictToRates: {
          'ui:widget': ReactHookFormAdapter,
          'ui:options': {
            ReactHookFormComponent: PromotionMultiSelect,
            props: {
              property: this.state.property,
              setRates: this.selectRatesInForm,
              activePromotions: this.state.activePromotions,
            },
          },
        },
      },
    },
    rooms: {
      ...uiSchemas(props).rooms,
      items: {
        ...uiSchemas(props).rooms.items,
        descriptionOverride: {
          ...uiSchemas(props).rooms.items.descriptionOverride,
          'ui:generatorContext': {
            propertyName: props.property.name,
            roomName: '',
            model: 'room-type',
            field: 'description',
          },
          'ui:options': {
            updateContext: roomDescriptionUpdateContext.bind(this),
          },
        },
        'ui:TitleFieldTemplate': roomTitleFieldTemplate.bind(this),
      },
    },
    roomMapping: {
      ...uiSchemas(props).roomMapping,
      items: {
        ...uiSchemas(props).roomMapping.items,
      },
    },
    videos: uiSchemas(props).videos,
    campaigns: {
      ...uiSchemas(props).campaigns,
    },
    holidayTypes: {
      ...uiSchemas(props).holidayTypes,
    },
  });

  selectRatesInForm = (index: number, selectedItems: Array<string>, append = false) => {
    const promoToBeChanged = this.state.activePromotions[index];
    const newRestrictions = append ? [...promoToBeChanged.restrictToRates, ...selectedItems] : selectedItems;

    const duplicateRates = newRestrictions.filter((item, index: number, array) => array.indexOf(item) !== index);

    if (duplicateRates.length > 0) {
      this.props.enqueueSnackbar(
        `Some duplicate rates were provided: ${duplicateRates.join(', ')}. They have been automatically deduped.`,
        {
          variant: 'warning',
        },
      );
    }

    const newPromotionsState = [
      ...this.state.property.promotions.map((p) => ({
        ...p,
        restrictToRates: p.promoId === promoToBeChanged.promoId ? uniq(newRestrictions) : p.restrictToRates,
      })),
    ];

    this.setState({
      property: {
        ...this.state.property,
        promotions: newPromotionsState,
      },
      activePromotions: newPromotionsState.filter((promotion) => !isArchivedPromotion(promotion)),
      archivedPromotions: newPromotionsState.filter((promotion) => isArchivedPromotion(promotion)),
      saveState: 'primary',
    });
  };

  selectRoomsInForm = (index: number, selectedItems: Array<string>, append = false) => {
    const promoToBeChanged = this.state.activePromotions[index];
    const newRestrictions = append ? [...promoToBeChanged.restrictToRooms, ...selectedItems] : selectedItems;

    const newPromotionsState = [
      ...this.state.property.promotions.map((p) => ({
        ...p,
        restrictToRooms: p.promoId === promoToBeChanged.promoId ? uniq(newRestrictions) : p.restrictToRooms,
      })),
    ];

    this.setState({
      property: {
        ...this.state.property,
        promotions: newPromotionsState,
      },
      activePromotions: newPromotionsState.filter((promotion) => !isArchivedPromotion(promotion)),
      archivedPromotions: newPromotionsState.filter((promotion) => isArchivedPromotion(promotion)),
      saveState: 'primary',
    });
  };

  stateToApiPropertyMapper(stateProperty: State['property']) {
    const sellMap = (sell: App.Bedbank.PropertyEdit['sell']) => ({
      ...sell,
      taxesAndFees: Number(sell.taxesAndFees ?? 0),
      propertyFees: Number(sell.propertyFees ?? 0),
      price: Number(sell.price ?? 0),
      value: Number(sell.value ?? 0),
      valueAdd: Number(sell.valueAdd ?? 0),
    });

    const mapRoomForPatch = (room: PatchableRoom) => {
      const { nameOverrideSource: _ignored, ...patchableRoom } = room;

      return {
        ...patchableRoom,
        sell: sellMap(patchableRoom.sell),
        // Remove mappedRooms from the payload because they can be many MBs in size, and they're not used.
        // On big properties they cause `413 Request Entity Too Large`
        mappedRooms: undefined,
      };
    };

    const disableMarginMaxChanged = !!stateProperty.disableMarginMax !== this.originalDisableMarginMax;
    const extractRoomNameInclusionsChanged =
      !!stateProperty.extractRoomNameInclusions !== this.originalExtractRoomNameInclusions;

    return {
      type: stateProperty.type,
      category: stateProperty.category,
      airport: stateProperty.airport,
      promoteAsBundle: !!stateProperty.promoteAsBundle,
      displayPricingAsPerPerson: !!stateProperty.displayPricingAsPerPerson,
      displayDecoupledRooms: !!stateProperty.displayDecoupledRooms,
      ...(disableMarginMaxChanged && { disableMarginMax: !!stateProperty.disableMarginMax }),
      ...(extractRoomNameInclusionsChanged && {
        extractRoomNameInclusions: !!stateProperty.extractRoomNameInclusions,
      }),
      suppliers: stateProperty.suppliers.map((supplier, index) => {
        const { hasDefaultMarkup, ...rest } = supplier;
        let supplierMarkup = rest.supplierMarkup;
        const unchangedDefaultValue =
          hasDefaultMarkup && this.originalSupplierMarkups[index].supplierMarkup === supplier.supplierMarkup;
        if (unchangedDefaultValue || !rest.supplierEnabled || supplier.supplier === Suppliers.expedia) {
          supplierMarkup = null;
        }
        return {
          supplier: rest.supplier,
          supplierEnabled: rest.supplierEnabled,
          metasearchEnabled: rest.metasearchEnabled,
          supplierMarkup,
        };
      }),

      isFlightEnabled: !!stateProperty.isFlightEnabled,
      isPreferred: !!stateProperty.isPreferred,
      freezeRooms: !!stateProperty.freezeRooms,
      status: stateProperty.status,
      domain: stateProperty.domain,
      tagline: stateProperty.tagline,
      description: stateProperty.description,
      additionalPromoContent: stateProperty.additionalPromoContent,
      promotions: stateProperty.promotions.map((promotion) => mapPromotion(promotion, isArchivedPromotion(promotion))),
      sellIsEnabled: !!stateProperty.sellIsEnabled,
      sell: sellMap(stateProperty.sell),
      rooms: stateProperty.rooms.map(mapRoomForPatch),
      nameOverride: stateProperty.nameOverride,
      facilitiesOverride: stateProperty.facilitiesOverride,
      attractionsOverride: stateProperty.attractionsOverride,
      finePrintOverride: stateProperty.finePrintOverride,
      locationTitleOverride: stateProperty.locationTitleOverride,
      highlights: stateProperty.highlights,
      images: stateProperty.images.map(requestImageMapper),
      videos: stateProperty.videos,
      campaigns: stateProperty.campaigns,
      holidayTypes: stateProperty.holidayTypes,
      logoId: stateProperty.logoId,
      updatedAt: stateProperty.updatedAt,
    };
  }

  getChangedRooms = (
    originalRooms: ReturnType<BedbankPropertiesEditForm['stateToApiPropertyMapper']>['rooms'],
    currentRooms: ReturnType<BedbankPropertiesEditForm['stateToApiPropertyMapper']>['rooms'],
  ) => {
    const originalRoomsById = new Map(originalRooms.map((room) => [room.id, room]));
    return currentRooms.filter((room) => {
      const originalRoom = originalRoomsById.get(room.id);
      if (!originalRoom) {
        return true;
      }
      return !isEqual(room, originalRoom);
    });
  };

  handleMainChange = (form) => {
    this.setState({
      property: {
        ...this.state.property,
        type: form.formData.type,
        category: form.formData.category,
        airport: form.formData.airport,
        promoteAsBundle: !!form.formData.promoteAsBundle,
        displayPricingAsPerPerson: !!form.formData.displayPricingAsPerPerson,
        displayDecoupledRooms: !!form.formData.displayDecoupledRooms,
        disableMarginMax: !!form.formData.disableMarginMax,
        extractRoomNameInclusions: !!form.formData.extractRoomNameInclusions,
        isFlightEnabled: !!form.formData.isFlightEnabled,
        suppliers: form.formData.suppliers,

        isPreferred: !!form.formData.isPreferred,
        status: form.formData.status,
        domain: form.formData.domain,
        tagline: form.formData.tagline,
        description: form.formData.description,
        additionalPromoContent: form.formData.additionalPromoContent,
        nameOverride: form.formData.nameOverride || null,
        facilitiesOverride: form.formData.facilitiesOverride,
        attractionsOverride: form.formData.attractionsOverride,
        finePrintOverride: form.formData.finePrintOverride,
        locationTitleOverride: form.formData.locationTitleOverride || null,
        highlights: form.formData.highlights,
        videos: form.formData.videos,
        logoId: form.formData.logoId,
      },
      saveState: 'primary',
    });
  };

  handleSellPricesChange = (form) => {
    this.setState({
      property: {
        ...this.state.property,
        sellIsEnabled: !!form.formData.sellIsEnabled,
        sell: form.formData.sell,
        rooms: this.state.property.rooms.map((room) => {
          const roomFormData = form.formData.rooms.find((r) => r.id === room.id);
          if (roomFormData) {
            return roomFormData;
          }
          return room;
        }),
      },
      saveState: 'primary',
    });
  };

  handleFreezeRooms = (freezeRooms) => {
    this.setState({
      property: {
        ...this.state.property,
        freezeRooms,
      },
      saveState: 'primary',
    });
  };

  handleRoomsChange = (form) => {
    this.setState({
      property: {
        ...this.state.property,
        rooms: form.formData,
      },
      saveState: 'primary',
    });
  };

  handlePromotionsChange = (form) => {
    const activePromotions = form.formData.map((promo) => {
      // Non-rate-code suppliers must not carry restrictToRates — svc-bedbank rejects it.
      // Clear any stale value carried over when the admin switched supplier away from EPS/HB.
      const supportsRateCodes = promo.supplier === Suppliers.expedia || promo.supplier === Suppliers.hotelbeds;
      return {
        ...promo,
        restrictToRates: supportsRateCodes ? (promo.restrictToRates ?? []) : [],
        inclusions: (promo.inclusions ?? []).map((inclusion) => ({
          text: inclusion?.text ?? '',
          category: inclusion?.category ?? '',
          icon: inclusion?.icon ?? '',
          isHighlighted: inclusion?.isHighlighted ?? false,
        })),
      };
    });

    this.setState({
      activePromotions,
      property: {
        ...this.state.property,
        // since promotion form data only contains unarchived promotions, we need to merge the new form data with the archived promotion for the new state
        promotions: [...activePromotions, ...this.state.archivedPromotions],
      },
      saveState: 'primary',
    });
  };

  handleVideosChange = (form) => {
    form.formData.forEach((video) => {
      if (video.url && (!video.id || !video.type)) {
        switch (true) {
          case video.url.includes('youtube.com'):
          case video.url.includes('youtub.be'):
            video.type = 'youtube';
            video.id = video.url.includes('v=') ? video.url.split('v=')[1] : video.url.split('/').pop();
            break;

          case video.url.includes('vimeo.com'):
            video.type = 'vimeo';
            video.id = video.url.split('/').pop();
            break;
        }
      }
    });

    this.setState({
      property: {
        ...this.state.property,
        videos: form.formData,
      },
      saveState: 'primary',
    });
  };

  handleTagsChange = (form, type: 'campaigns' | 'holidayTypes') => {
    this.setState({
      property: {
        ...this.state.property,
        [type]: form.formData,
      },
      saveState: 'primary',
    });
  };

  handleImagesChange = (images) => {
    this.setState({
      property: {
        ...this.state.property,
        images,
      },
      saveState: 'primary',
    });
  };

  handleLogoChange = (logoId) => {
    this.setState({
      property: {
        ...this.state.property,
        logoId,
      },
      saveState: 'primary',
    });
  };

  handleRoomMappingChange = ({ update, remove, automappingFlagUpdate }) => {
    const hasPendingMappings =
      (update?.length ?? 0) > 0 || (remove?.length ?? 0) > 0 || (automappingFlagUpdate?.length ?? 0) > 0;
    this.setState({
      roomMappings: hasPendingMappings ? { update, remove, automappingFlagUpdate } : null,
      saveState: 'primary',
    });
  };

  onSubmit = (form) => {
    form.errors = [];
    form.errorSchema = {};

    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const self = this;

    if (this.state.saveState !== 'info') {
      if (this.state.roomMappings) {
        const { update, remove, automappingFlagUpdate } = this.state.roomMappings;
        this.setState(
          {
            saveState: 'info',
          },
          async () => {
            try {
              // Delete should happens before update, to account for the flow where an existing mapped room is deleted, then re-added to the same property room.
              if (remove?.length > 0) {
                await bedbankService.deleteMappedRooms(this.state.property.id, remove);
              }
              if (update?.length > 0) {
                await bedbankService.createMappedRooms(this.state.property.id, update);
              }
              if (automappingFlagUpdate?.length > 0) {
                await bedbankService.setAutomappingFlag(this.state.property.id, automappingFlagUpdate);
              }

              // Reload to get latest data
              window.location.reload();
            } catch (error) {
              this.setState({ saveState: 'error' });
              this.props.enqueueSnackbar('Failed to save room mappings', { variant: 'error' });
              reportError(error);
            }
          },
        );
        return;
      }

      this.setState(
        {
          saveState: 'info',
        },
        () => {
          const updatedProperty = this.stateToApiPropertyMapper(self.state.property);
          const changedRooms = this.getChangedRooms(this.originalRooms, updatedProperty.rooms);

          if (changedRooms.length === 0) {
            delete updatedProperty.rooms;
          } else {
            updatedProperty.rooms = changedRooms;
          }

          bedbankService
            .patchPropertyEdit(this.props.property.id, updatedProperty)
            .then((response) => {
              this.originalSupplierMarkups = response.result.suppliers;
              this.originalDisableMarginMax = !!response.result.disableMarginMax;
              this.originalExtractRoomNameInclusions = !!response.result.extractRoomNameInclusions;
              this.originalRooms = this.stateToApiPropertyMapper(apiToStatePropertyMapper(response.result)).rooms;
              self.setState({
                property: {
                  ...response.result,
                  promotions: response.result.promotions.map((promotion) =>
                    mapPromotion(promotion, isArchivedPromotion(promotion)),
                  ),
                },
                activePromotions: response.result.promotions.filter((promotion) => !isArchivedPromotion(promotion)),
                archivedPromotions: response.result.promotions.filter((promotion) => isArchivedPromotion(promotion)),
                schemas: {
                  ...this.state.schemas,
                },
                saveState: 'success',
              });
              self.props.setProperty(response.result);
            })
            .catch(function (e) {
              if (e.status === 409) {
                const errorMessage =
                  'The property has already been updated. Needs to refresh the page and try to save it again.';

                self.setState({
                  saveState: 'error',
                  alertState: {
                    message: errorMessage,
                  },
                });
                self.props.enqueueSnackbar(errorMessage, { variant: 'error' });
              } else {
                self.setState({ saveState: 'error' });
                self.props.enqueueSnackbar(e.message || 'Unable to save bedbank property!', {
                  variant: 'error',
                });
              }

              reportError(e);
            });
        },
      );
    }
  };

  fetchCalendarData = () => {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const self = this;

    this.setState(
      {
        calendarFetching: true,
      },
      () => {
        bedbankService
          .getAllCalendar(this.props.property.id)
          .then((response) => {
            self.setState({
              calendarFetching: false,
              calendar: { rooms: response.result.rooms },
            });
          })
          .catch(function (e) {
            self.setState({ calendarFetching: false });
            reportError(e);
          });
      },
    );
  };

  handleTabChange = (event: SyntheticEvent, newValue: number) => {
    this.setState({
      tabIndex: newValue,
    });
    window.location.hash = String(newValue);
  };

  createPropertyImage = async (imageServiceId) => {
    const { result: imageData } = await bedbankService.createPropertyImage(this.props.property.id, {
      imageServiceId,
    });

    return imageData;
  };

  createRoomImage = async (roomId, imageServiceId) => {
    const { result: imageData } = await bedbankService.createRoomImage(roomId, {
      imageServiceId,
    });

    return imageData;
  };

  handleRoomImagesChange = (roomId, images) => {
    this.setState({
      property: {
        ...this.state.property,
        rooms: this.state.property.rooms.map((room) => {
          if (room.id === roomId) {
            return {
              ...room,
              images,
            };
          }
          return room;
        }),
      },
      saveState: 'primary',
    });
  };

  toggleAllRoomImages = () => {
    const isAllExpanded = this.state.roomsExpansionStatuses.some((isExpanded) => isExpanded);
    this.setState({
      roomsExpansionStatuses: this.state.roomsExpansionStatuses.map(() => !isAllExpanded),
    });
  };

  toggleRoomImages = (index) => {
    const newState = [...this.state.roomsExpansionStatuses];
    newState[index] = !this.state.roomsExpansionStatuses[index];
    this.setState({
      roomsExpansionStatuses: newState,
    });
  };

  handlePromotionDelete = (index) => {
    const promoToBeDeleted = this.state.activePromotions[index];
    const newPromotionsState = [
      ...this.state.property.promotions.map((p) => ({
        ...p,
        deletedAt: p.promoId === promoToBeDeleted.promoId ? new Date().toISOString() : p.deletedAt,
      })),
    ];

    this.setState({
      property: {
        ...this.state.property,
        promotions: newPromotionsState,
      },
      activePromotions: newPromotionsState.filter((promotion) => !isArchivedPromotion(promotion)),
      archivedPromotions: newPromotionsState.filter((promotion) => isArchivedPromotion(promotion)),
    });
  };

  handleArchivedPromotionUpdate = (extendedPromotion: App.Bedbank.PromotionsEdit) => {
    const existingPromotions = this.state.archivedPromotions.filter(
      (promotion) => promotion.promoId !== extendedPromotion.promoId,
    );

    // check to make sure that new dates make the promotion active before moving to activePromotions.
    extendedPromotion.deletedAt = undefined;
    const isActivePromotion = !isArchivedPromotion(extendedPromotion);

    this.setState({
      property: {
        ...this.state.property,
        promotions: [extendedPromotion, ...existingPromotions].map((promotion) =>
          mapPromotion(promotion, isArchivedPromotion(promotion)),
        ),
      },
      activePromotions: isActivePromotion
        ? [...this.state.activePromotions, extendedPromotion]
        : this.state.activePromotions,
      archivedPromotions: isActivePromotion ? existingPromotions : [extendedPromotion, ...existingPromotions],
    });
  };

  /*
    Custom template for promotion tab. When deleting a promotion, the promotion is not deleted from the array, but instead deletedAt is set to the current date string.
    If there's an easier way to override the delete button's behaviour without having to override the entire template, please do so.
   */
  arrayFieldTemplate = (props: ArrayFieldTemplateProps) => (
    <PromotionsArrayFieldTemplate {...props} handleDelete={this.handlePromotionDelete} />
  );

  render() {
    const isAllRoomImagesExpanded = this.state.roomsExpansionStatuses.some((isExpanded) => isExpanded);
    const showSaveButton = !hideSaveButtonTabs.includes(this.state.tabIndex);

    return (
      <Box sx={{ paddingTop: 3 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={this.state.tabIndex} onChange={this.handleTabChange} aria-label="basic tabs">
            <Tab label="Main" sx={{ minWidth: 0, px: 1.5 }} {...a11yProps(0)} />
            <Tab label="Promotions" sx={{ minWidth: 0, px: 1.5 }} {...a11yProps(1)} />
            <Tab label="Rooms" sx={{ minWidth: 0, px: 1.5 }} {...a11yProps(2)} />
            <Tab label="Room mapping" sx={{ minWidth: 0, px: 1.5 }} {...a11yProps(3)} />
            <Tab label="Room Images" sx={{ minWidth: 0, px: 1.5 }} {...a11yProps(4)} />
            <Tab label="Sell Prices" sx={{ minWidth: 0, px: 1.5 }} {...a11yProps(5)} />
            <Tab label="Property Images" sx={{ minWidth: 0, px: 1.5 }} {...a11yProps(6)} />
            <Tab label="Marketing & Tags" sx={{ minWidth: 0, px: 1.5 }} {...a11yProps(7)} />
            <Tab label="Archived Promotions" sx={{ minWidth: 0, px: 1.5 }} {...a11yProps(8)} />
            <Tab label="Search Ranking" sx={{ minWidth: 0, px: 1.5 }} {...a11yProps(9)} />
            <Tab label="Rates" sx={{ minWidth: 0, px: 1.5 }} {...a11yProps(10)} />
            <Tab label="Rank" sx={{ minWidth: 0, px: 1.5 }} {...a11yProps(11)} />
            <Tab label="Reviews" sx={{ minWidth: 0, px: 1.5 }} {...a11yProps(12)} />
          </Tabs>
        </Box>

        <TabPanel value={this.state.tabIndex} index={0}>
          <BedbankContext.Provider value={this.state}>
            <Form
              schema={this.state.schemas.main}
              validator={validator}
              formData={this.state.property}
              uiSchema={this.uiSchemas(this.props).main}
              onSubmit={this.onSubmit}
              onChange={this.handleMainChange}
            />

            <Typography pt={1} variant="h5">
              Logo
            </Typography>
            <ImageUploadField
              label="logo"
              value={this.state.property.logoId}
              multiple={false}
              onUpload={(field_key, newValue) => {
                this.handleLogoChange(newValue);
              }}
            />
          </BedbankContext.Provider>
        </TabPanel>

        <TabPanel value={this.state.tabIndex} index={1}>
          <Form
            schema={this.state.schemas.promotions}
            validator={validator}
            formData={this.state.activePromotions}
            uiSchema={this.uiSchemas(this.props).promotions}
            onSubmit={this.onSubmit}
            onChange={this.handlePromotionsChange}
            templates={{ ArrayFieldTemplate: this.arrayFieldTemplate }}
          />
        </TabPanel>

        <TabPanel value={this.state.tabIndex} index={2}>
          <BedbankRoomManagement
            property={this.state.property}
            promotions={this.state.activePromotions}
            handleChange={this.handleRoomsChange}
            handleFreezeRooms={this.handleFreezeRooms}
            onSubmit={this.onSubmit}
          />
        </TabPanel>

        <TabPanel value={this.state.tabIndex} index={3}>
          <BedbankRoomMapping
            property={this.state.property}
            handleChange={this.handleRoomMappingChange}
            onForceDecoupleRoomsClick={this.props.onForceDecoupleRoomsClick}
          />
        </TabPanel>

        <TabPanel value={this.state.tabIndex} index={4}>
          {this.state.property.rooms.length && (
            <Box mb={1}>
              <Button variant="text" size="small" onClick={this.toggleAllRoomImages}>
                {isAllRoomImagesExpanded ? 'Collapse All Rooms' : 'Expand All Rooms'}
              </Button>
            </Box>
          )}
          <Box>
            {this.state.property.rooms.map((room, index) => (
              <Accordion
                variant="outlined"
                expanded={this.state.roomsExpansionStatuses[index]}
                key={room.id}
                onChange={() => {
                  this.toggleRoomImages(index);
                }}
              >
                <AccordionSummary expandIcon={<ExpandMore />} aria-controls={room.id} id={room.id}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      width: '100%',
                      mr: 2,
                    }}
                  >
                    <Typography>
                      {room.name}
                      {room.externalId && (
                        <Typography component="span" variant="body2" sx={{ ml: 1, color: 'text.secondary' }}>
                          ({room.externalId})
                        </Typography>
                      )}
                    </Typography>
                    <Chip
                      label={`${room.images.filter((img) => !img.hidden && !img.is_deleted).length} images`}
                      size="small"
                      variant="outlined"
                    />
                  </Box>
                </AccordionSummary>

                <AccordionDetails>
                  <ImagesForm
                    images={room.images}
                    onImagesChange={(images) => this.handleRoomImagesChange(room.id, images)}
                    onCreateImage={(imageServiceId) => this.createRoomImage(room.id, imageServiceId)}
                  />
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        </TabPanel>

        <TabPanel value={this.state.tabIndex} index={5}>
          <Form
            schema={this.state.schemas.sellPrices}
            validator={validator}
            formData={{
              sellIsEnabled: this.state.property.sellIsEnabled,
              sell: this.state.property.sell,
              rooms: this.state.property.rooms.filter((x) =>
                (this.state.property.promotions ?? [])
                  .filter((p) => !isArchivedPromotion(p))
                  .flatMap((p) => p.restrictToRooms ?? [])
                  .includes(x.id),
              ),
            }}
            uiSchema={this.uiSchemas(this.props).sellPrices}
            onSubmit={this.onSubmit}
            onChange={this.handleSellPricesChange}
          >
            <></>
          </Form>
        </TabPanel>

        <TabPanel value={this.state.tabIndex} index={6}>
          <ImagesForm
            images={this.state.property.images}
            onImagesChange={this.handleImagesChange}
            onCreateImage={this.createPropertyImage}
          />
          <Form
            schema={this.state.schemas.videos}
            validator={validator}
            formData={this.state.property.videos}
            uiSchema={getVideosSchema()}
            onSubmit={this.onSubmit}
            onChange={this.handleVideosChange}
          ></Form>
        </TabPanel>

        <TabPanel value={this.state.tabIndex} index={7}>
          {this.state.marketingTagsLoading ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
              <CircularProgress />
            </Box>
          ) : (
            <Stack direction="column" gap={4}>
              <Form
                schema={this.state.schemas.holidayTypes}
                validator={validator}
                formData={this.state.property.holidayTypes}
                uiSchema={this.uiSchemas(this.props).holidayTypes}
                onSubmit={this.onSubmit}
                onChange={(form) => this.handleTagsChange(form, 'holidayTypes')}
              />
              <Form
                schema={this.state.schemas.campaigns}
                validator={validator}
                formData={this.state.property.campaigns}
                uiSchema={this.uiSchemas(this.props).campaigns}
                onSubmit={this.onSubmit}
                onChange={(form) => this.handleTagsChange(form, 'campaigns')}
              />
            </Stack>
          )}
        </TabPanel>

        <TabPanel value={this.state.tabIndex} index={8}>
          <ArchivedPromotionPane
            promotions={this.state.archivedPromotions}
            canUpdateArchivedPromotions={this.state.activePromotions.length === 0}
            handleArchivedPromotionUpdate={this.handleArchivedPromotionUpdate}
          />
        </TabPanel>

        <TabPanel value={this.state.tabIndex} index={9}>
          <Paper className="branded-panel">
            <SearchOrderingPane vertical="hotel" offerId={this.state.property.id} />
          </Paper>
        </TabPanel>

        <TabPanel value={this.state.tabIndex} index={10}>
          <RateInfo propertyId={this.props.property.id} />
        </TabPanel>

        <TabPanel value={this.state.tabIndex} index={11}>
          <RankPane propertyId={this.props.property.id} />
        </TabPanel>

        <TabPanel value={this.state.tabIndex} index={12}>
          <ReviewsPane propertyId={this.props.property.id} />
        </TabPanel>

        {this.state.alertState && (
          <Alert severity="error">
            <AlertTitle>Error</AlertTitle>
            {this.state.alertState.message}
          </Alert>
        )}

        {showSaveButton && (
          <div className="button-container">
            <Button variant="contained" color={this.state.saveState} onClick={this.onSubmit}>
              {buttonMessages[this.state.saveState]}
            </Button>
          </div>
        )}
      </Box>
    );
  }
}

export default withSnackbar(BedbankPropertiesEditForm);
